import { getServiceUrl } from '../api.config';
import { InsightsResult, InsightsSubstitutionResult } from '../types';
import { ApiClientOptions } from '../types/api';
import ApiClient from '../utils/ApiClient';
import qs from '../utils/query';
import {
  isBoolean,
  isNotEmpty,
  isNotEmptyString,
  isPositiveInteger,
} from '../utils/validators';

export type SearchQueryType = Partial<{
  advancedSearch: any;
  aggregations: any;
  gtinsIn: string[];
  productIdentifiersIn: string[];
  productIdsIn: any[];
  productVersionIdsIn: any[];
  queryName: string;
  manufacturerIds: any[];
  internalMultiCategorizationIds: any[];
  shared_with_organization_ids: any[];
  targetProductStatus: any;
  lifecycle: any;
  allowTextileVariants: any;
  allowNotConsumerUnits: any;
  allowHeterogeneousLogisticalUnits: any;
  filterOwnersIn: any;
  suggestedOwnerIds: any;
  hasSuggestedOwner: any;
  facets: any;
  sourceInclude: any;
  sourceExclude: any;
  isReviewPending: boolean;
  isReviewFirstShare: boolean;
  reviewActionStatuses: any[];
}>;

export const buildProductVersionListFilters = ({
  queries = {},
}: {
  queries: SearchQueryType;
}) => {
  const payload: any = {};

  // search
  if (isNotEmpty(queries.gtinsIn)) {
    payload.filter_gtins_in = queries.gtinsIn;
    payload.filter_variant_gtins_in = queries.gtinsIn;
  }
  if (isNotEmpty(queries.productIdentifiersIn)) {
    payload.filter_product_identifiers_in = queries.productIdentifiersIn;
  }
  if (isNotEmpty(queries.productIdsIn)) {
    payload.filter_product_id_in = queries.productIdsIn;
  }
  if (isNotEmpty(queries.productVersionIdsIn)) {
    payload.filter_id_in = queries.productVersionIdsIn;
  }
  if (isNotEmptyString(queries.queryName)) {
    payload.q = queries.queryName;
  }
  if (queries.advancedSearch) {
    payload.advanced_search = queries.advancedSearch;
  }

  // Retailer List Share
  if (isNotEmpty(queries.manufacturerIds)) {
    payload.filter_manufacturer_id_in = queries.manufacturerIds;
  }
  if (isNotEmpty(queries.internalMultiCategorizationIds)) {
    payload.filter_internal_multi_categorization_in =
      queries.internalMultiCategorizationIds;
  }
  if (isNotEmpty(queries.shared_with_organization_ids)) {
    payload.filter_shared_with_organization_ids =
      queries.shared_with_organization_ids;
  }
  if (isNotEmpty(queries.targetProductStatus)) {
    payload.filter_target_productstatus_in = queries.targetProductStatus;
  }
  if (isNotEmpty(queries.lifecycle)) {
    payload.filter_lifecycle_in = queries.lifecycle;
  }

  // For Third parties
  if (isBoolean(queries.allowNotConsumerUnits)) {
    payload.allow_not_consumer_units = queries.allowNotConsumerUnits;
  }

  if (isBoolean(queries.allowTextileVariants)) {
    payload.allow_textile_variants = queries.allowTextileVariants;
  }

  if (isBoolean(queries.allowHeterogeneousLogisticalUnits)) {
    payload.allow_heterogeneous_logistical_units =
      queries.allowHeterogeneousLogisticalUnits;
  }

  // Owners
  if (isNotEmpty(queries.filterOwnersIn)) {
    payload.filter_owners_in = queries.filterOwnersIn;
  }

  // Suggested owners
  if (isNotEmpty(queries.suggestedOwnerIds)) {
    payload.filter_suggested_owner_in = queries.suggestedOwnerIds;
  }
  if (isBoolean(queries.hasSuggestedOwner)) {
    payload.filter_has_suggested_owner = queries.hasSuggestedOwner;
  }

  // Statuses
  if (isNotEmpty(queries.facets)) {
    payload.facets = queries.facets;
  }
  if (isNotEmpty(queries.aggregations)) {
    payload.aggregations = queries.aggregations;
  }

  // Review
  if (isNotEmpty(queries.isReviewPending)) {
    payload.review_is_pending = queries.isReviewPending ? 1 : 0;
  }
  if (isNotEmpty(queries.isReviewFirstShare)) {
    payload.review_is_first_share = queries.isReviewFirstShare ? 1 : 0;
  }
  if (isNotEmpty(queries.reviewActionStatuses)) {
    payload.review_action_statuses = queries.reviewActionStatuses;
  }

  // Optimization with fields
  if (isNotEmpty(queries.sourceInclude)) {
    payload.filter_source_include = queries.sourceInclude;
  }
  if (isNotEmpty(queries.sourceExclude)) {
    payload.filter_source_exclude = queries.sourceExclude;
  }

  return payload;
};

export default class SearchApi extends ApiClient {
  public constructor(options?: ApiClientOptions) {
    super(getServiceUrl('search'), options);
  }

  // =============================================================================
  //                            Search APIs
  // =============================================================================

  public GetProductVersionExplorer(
    field1: any,
    field2: any,
    jsonBody: any = {},
  ) {
    const query = '/search/v1/productversionexplorer';
    return this.post(query, { field1, field2, ...jsonBody });
  }

  public GetProductVersionList({
    queries = {},
    sort = {},
    offset,
    limit,
  }: {
    queries: SearchQueryType;
    sort: any;
    offset?: number;
    limit?: number;
    cache?: boolean;
  }) {
    const query = '/search/v2/productversion';
    // Use buildProductVersionListFilters to mutualize params with export
    const payload = buildProductVersionListFilters({ queries });

    // offset / limit
    if (isPositiveInteger(offset)) {
      payload.offset = offset;
    }
    if (isPositiveInteger(limit)) {
      payload.limit = limit;
    }

    // Sorting
    if (
      sort &&
      (isNotEmptyString(sort.by) || !!sort.advancedSortBy) &&
      isNotEmptyString(sort.order)
    ) {
      payload.sort_by = sort.by;
      payload.sort_order = sort.order;
      payload.advanced_sort_by = sort.advancedSortBy;
    }

    return this.post(query, payload, null);
  }

  public GetProductInsights(productVersionId: number) {
    const query = '/search/v1/productversion/insights/nutrition';
    const params = { pv_id: productVersionId };
    return this.get<InsightsResult>(query, qs.stringify(params, true));
  }

  public GetSourcingList({ queries = {}, sort = {}, offset, limit }: any) {
    const query = '/search/v2/sourcing';
    // Use buildProductVersionListFilters to mutualize params with export
    const payload = buildProductVersionListFilters({ queries });

    // offset / limit
    if (isPositiveInteger(offset)) {
      payload.offset = offset;
    }
    if (isPositiveInteger(limit)) {
      payload.limit = limit;
    }

    // Sorting
    if (
      sort &&
      (isNotEmptyString(sort.by) || !!sort.advancedSortBy) &&
      isNotEmptyString(sort.order)
    ) {
      payload.sort_by = sort.by;
      payload.sort_order = sort.order;
      payload.advanced_sort_by = sort.advancedSortBy;
    }

    return this.post(query, payload, null);
  }

  public GetProductSubstitutesFromDimension(
    productVersionId: number,
    dimension: string,
    language: string,
    numberOfProducts: number,
  ) {
    const query = '/search/v1/productversion/insights/substitute';
    const params = {
      productversion_id: productVersionId,
      dimension,
      language,
      number_of_products: numberOfProducts,
    };
    return this.get<InsightsSubstitutionResult>(
      query,
      qs.stringify(params, true),
    );
  }

  public GetAssignationList({ queries = {}, sort = {}, offset, limit }: any) {
    const query = '/search/v2/assignation';
    const payload: any = {};

    if (isNotEmpty(queries.gtinsIn)) {
      payload.filter_gtins_in = queries.gtinsIn;
    }

    if (isNotEmptyString(queries.queryName)) {
      payload.q = queries.queryName;
    }
    if (queries.filters) {
      payload.filters = queries.filters;
    }
    if (queries.advancedSearch) {
      payload.advanced_search = queries.advancedSearch;
    }
    if (isNotEmpty(queries.aggregations)) {
      payload.aggregations = queries.aggregations;
    }
    if (isNotEmpty(sort) && isNotEmpty(sort.advancedSortBy)) {
      payload.sort = { ...sort.advancedSortBy };
      if (isNotEmptyString(sort.order)) {
        payload.sort.order = sort.order;
      }
    }
    // offset / limit
    if (isPositiveInteger(offset)) {
      payload.offset = offset;
    }
    if (isPositiveInteger(limit)) {
      payload.limit = limit;
    }
    return this.post(query, payload, null);
  }

  public GetFieldSuggestionList(data: any) {
    // Advanced search on field suggestion
    const route = '/search/v1/fieldsuggestion';
    return this.post(route, data);
  }

  public ReindexOrganization({ organizationIds, indexName }: any) {
    const route = '/search/v1/index/full';
    return this.post(route, {
      index_name: indexName,
      organization_ids: organizationIds,
    });
  }
}
